import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  Button,
  LinearProgress,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material'
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Info,
  EmojiEvents,
} from '@mui/icons-material'
import {
  useGetFrequencyComparisonQuery,
  useGetFrequencyGoalsQuery,
  useUpdateFrequencyGoalsMutation,
  type DateRange,
  type FrequencyGoals,
} from '../../store/api/analyticsApi'

interface FrequencyTrackingProps {
  dateRange: DateRange
}

function TrendChip({ direction }: { direction: 'increasing' | 'decreasing' | 'stable' }) {
  if (direction === 'increasing') {
    return (
      <Chip
        icon={<TrendingUp />}
        label="Increasing"
        color="warning"
        size="small"
        variant="outlined"
      />
    )
  }
  if (direction === 'decreasing') {
    return (
      <Chip
        icon={<TrendingDown />}
        label="Decreasing"
        color="success"
        size="small"
        variant="outlined"
      />
    )
  }
  return (
    <Chip
      icon={<TrendingFlat />}
      label="Stable"
      color="default"
      size="small"
      variant="outlined"
    />
  )
}

function GoalProgress({
  label,
  current,
  goal,
  tooltip,
}: {
  label: string
  current: number
  goal: number
  tooltip: string
}) {
  const pct = Math.min(100, Math.round((current / goal) * 100))
  const isOver = current > goal
  const color = isOver ? 'error' : pct >= 80 ? 'warning' : 'primary'

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Tooltip title={tooltip} placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
            <Typography variant="body2">{label}</Typography>
            <Info sx={{ fontSize: 14, color: 'text.secondary' }} />
          </Box>
        </Tooltip>
        <Typography variant="body2" color={isOver ? 'error.main' : 'text.secondary'}>
          {current} / {goal}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ height: 8, borderRadius: 1 }}
      />
      {isOver && (
        <Typography variant="caption" color="error.main" sx={{ mt: 0.25, display: 'block' }}>
          Goal exceeded by {current - goal}
        </Typography>
      )}
    </Box>
  )
}

const FrequencyTracking: React.FC<FrequencyTrackingProps> = ({ dateRange }) => {
  const { data: comparison, isLoading: compLoading } = useGetFrequencyComparisonQuery({
    dateRange,
  })
  const { data: goals } = useGetFrequencyGoalsQuery()
  const [updateGoals, { isLoading: saving }] = useUpdateFrequencyGoalsMutation()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Partial<FrequencyGoals>>({})

  const handleEdit = () => {
    setDraft({
      dailyGoal: goals?.dailyGoal ?? undefined,
      weeklyGoal: goals?.weeklyGoal ?? undefined,
      monthlyGoal: goals?.monthlyGoal ?? undefined,
    })
    setEditing(true)
  }

  const handleSave = async () => {
    const payload: Partial<FrequencyGoals> = {}
    const toNum = (v: unknown) => {
      const n = parseInt(String(v), 10)
      return isNaN(n) || n < 1 ? null : n
    }
    if ('dailyGoal' in draft) payload.dailyGoal = toNum(draft.dailyGoal)
    if ('weeklyGoal' in draft) payload.weeklyGoal = toNum(draft.weeklyGoal)
    if ('monthlyGoal' in draft) payload.monthlyGoal = toNum(draft.monthlyGoal)
    await updateGoals(payload)
    setEditing(false)
  }

  const handleCancel = () => {
    setEditing(false)
    setDraft({})
  }

  return (
    <Box>
      {/* Comparative frequency card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Frequency Comparison</Typography>
            {comparison && <TrendChip direction={comparison.change.direction} />}
          </Box>

          {compLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : comparison ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      This Period
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {comparison.current.sessions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      sessions
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Previous Period
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="text.secondary">
                      {comparison.previous.sessions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      sessions
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Avg / Week (now)
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {comparison.current.avgPerWeek.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      cigars/week
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Change
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={
                        comparison.change.sessions > 0
                          ? 'warning.main'
                          : comparison.change.sessions < 0
                          ? 'success.main'
                          : 'text.primary'
                      }
                    >
                      {comparison.change.sessions > 0 ? '+' : ''}
                      {comparison.change.sessions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      vs prev period
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Time of day breakdown */}
              <Typography variant="subtitle2" gutterBottom>
                Time of Day Breakdown
              </Typography>
              <Grid container spacing={1}>
                {(
                  [
                    { label: 'Morning', key: 'morning', time: '5am–12pm' },
                    { label: 'Afternoon', key: 'afternoon', time: '12pm–5pm' },
                    { label: 'Evening', key: 'evening', time: '5pm–9pm' },
                    { label: 'Night', key: 'night', time: '9pm–5am' },
                  ] as const
                ).map(({ label, key, time }) => {
                  const count = comparison.timeOfDay[key]
                  const total = Object.values(comparison.timeOfDay).reduce((a, b) => a + b, 0)
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0
                  return (
                    <Grid item xs={6} sm={3} key={key}>
                      <Tooltip title={time}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                            cursor: 'help',
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {label}
                          </Typography>
                          <Typography variant="caption" color="primary.main">
                            {pct}%
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                  )
                })}
              </Grid>
            </>
          ) : (
            <Alert severity="info">No session data available for this period.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Frequency goals card */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmojiEvents color="primary" />
              <Typography variant="h6">Frequency Goals</Typography>
            </Box>
            {!editing && (
              <Button size="small" onClick={handleEdit}>
                {goals?.dailyGoal || goals?.weeklyGoal || goals?.monthlyGoal ? 'Edit Goals' : 'Set Goals'}
              </Button>
            )}
          </Box>

          {editing ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set optional limits to track your smoking frequency. Leave blank to clear a goal.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Daily goal (cigars/day)"
                    type="number"
                    size="small"
                    fullWidth
                    value={draft.dailyGoal ?? ''}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, dailyGoal: e.target.value ? parseInt(e.target.value, 10) : undefined }))
                    }
                    inputProps={{ min: 1, max: 1000 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Weekly goal (cigars/week)"
                    type="number"
                    size="small"
                    fullWidth
                    value={draft.weeklyGoal ?? ''}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, weeklyGoal: e.target.value ? parseInt(e.target.value, 10) : undefined }))
                    }
                    inputProps={{ min: 1, max: 1000 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Monthly goal (cigars/month)"
                    type="number"
                    size="small"
                    fullWidth
                    value={draft.monthlyGoal ?? ''}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        monthlyGoal: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      }))
                    }
                    inputProps={{ min: 1, max: 1000 }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="small" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : goals?.dailyGoal || goals?.weeklyGoal || goals?.monthlyGoal ? (
            <Box>
              {goals.dailyGoal && comparison && (
                <GoalProgress
                  label="Daily"
                  current={Math.round(comparison.current.avgPerDay)}
                  goal={goals.dailyGoal}
                  tooltip="Average cigars per day in the current period vs your daily goal"
                />
              )}
              {goals.weeklyGoal && comparison && (
                <GoalProgress
                  label="Weekly"
                  current={Math.round(comparison.current.avgPerWeek)}
                  goal={goals.weeklyGoal}
                  tooltip="Average cigars per week in the current period vs your weekly goal"
                />
              )}
              {goals.monthlyGoal && comparison && (
                <GoalProgress
                  label="Monthly"
                  current={Math.round(comparison.current.avgPerWeek * 4.33)}
                  goal={goals.monthlyGoal}
                  tooltip="Estimated cigars per month (weekly avg × 4.33) vs your monthly goal"
                />
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No goals set. Click &quot;Set Goals&quot; to track your smoking frequency limits.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default FrequencyTracking
